import { useSession } from 'next-auth/react';
import { useRef } from 'react';
import useSWR from 'swr';

import { DURATION, NEXT_AUTH_STATUSES, REFRESH_HEADER_KEY } from '@/lib/constants';
import { ShipmentModel } from '@/lib/models/shipment/types';
import { AsyncDataInterface } from '@/types/context-types';
import { request } from '@/utils/api/request';
import { getShipmentDetailUrl } from '@/utils/helpers/api-urls';

const ERROR_MESSAGE = 'Error fetching shipment';
const refreshHeaders = {
  [REFRESH_HEADER_KEY]: 'true',
};

interface UseShipmentDetailsProps {
  shipmentId?: string | null;
}

interface UseShipmentDetails extends AsyncDataInterface {
  forceDataRefresh: () => void;
  shipment: ShipmentModel | null;
  isRefreshing: boolean;
}

interface ShipmentsApiResponse {
  success: boolean;
  shipment?: ShipmentModel;
  message?: string;
}

export const useShipmentDetails = ({ shipmentId }: UseShipmentDetailsProps): UseShipmentDetails => {
  const headers = useRef({});
  const { status } = useSession();
  const shouldSearch = status === NEXT_AUTH_STATUSES.AUTHENTICATED && shipmentId;

  const { isLoading, isValidating, error, data, mutate } = useSWR<ShipmentsApiResponse>(
    shouldSearch ? getShipmentDetailUrl(shipmentId) : null,
    (url) => request(url, undefined, 'GET', headers.current),
    {
      revalidateOnFocus: false,
      errorRetryCount: 2,
      dedupingInterval: DURATION.IN_MILLISECONDS.TEN_MINS,
      onSuccess() {
        // Reset refresh headers when we get a response
        headers.current = {};
      },
    },
  );

  const errorMessage = error ? ERROR_MESSAGE : undefined;

  const forceDataRefresh = () => {
    headers.current = refreshHeaders;
    mutate();
  };

  return {
    isLoading,
    isRefreshing: isValidating,
    shipment: data?.shipment || null,
    error: errorMessage,
    forceDataRefresh,
  };
};
